import { useEffect, useMemo, useState } from "react";
import { AlertCircle, CircleCheck, Clock, CreditCard, Download } from "lucide-react";
import { Badge, Button, Card } from "@wa/ui";
import { PAYMENT_METHOD_TOKEN } from "@wa/core/payment-methods";
import { useI18n, useT } from "../../../i18n/I18nProvider";
import {
  portal, pdfHref,
  type PortalInvoiceDetail, type PortalPayable, type PortalPayment,
} from "../../../lib/portal";
import { fmtDate, fmtAmount, invoiceStatusVariant } from "../../../lib/format";
import { relativeDay } from "../../../lib/due";
import { DocLayout, DocSkeleton, Figure, LineItems, PageHead, Totals } from "../../ui";
import { isOverdue, outstandingCents } from "./lib";

type Translate = ReturnType<typeof useT>;

/**
 * One invoice, as a page: the document on the left, what is owed and the way
 * to pay it on the right.
 *
 * Read-only, and that is the whole difference between this screen and a quote:
 * an invoice is a statement, a quote is a question. The one thing the reader
 * can DO here is pay, and that button is drawn only on the server's word - see
 * `portal.invoicePayable`.
 *
 * It also prints three things the old dialog had in hand and did not show: the
 * payments already received, the notes and the terms. A client who paid half
 * and sees only "outstanding" has no way to check which half arrived.
 */
export function InvoicePage({ id, onBack }: { id: string; onBack: () => void }) {
  const t = useT();
  const { locale } = useI18n();
  const [detail, setDetail] = useState<PortalInvoiceDetail | null>(null);
  const [payable, setPayable] = useState<PortalPayable | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const now = useMemo(() => new Date(), []);

  useEffect(() => {
    let live = true;
    // An id this reader may not open answers 404, and the answer is the list
    // rather than a page of skeletons that never fills. Clearing the address
    // also stops a stale bookmark from re-opening an invoice that is not theirs.
    void portal.invoice(id).then((d) => { if (live) setDetail(d); }).catch(() => { if (live) onBack(); });
    // A separate, failure-tolerant read. If it does not answer, the invoice and
    // its PDF are still on screen: a card button is an extra, and losing it
    // must not cost the client the document.
    void portal.invoicePayable(id).then((p) => { if (live) setPayable(p); }).catch(() => {});
    return () => {
      live = false;
    };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function pay() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await portal.payInvoice(id);
      // Leaving the page rather than opening a tab: a card form in a popup is
      // the shape a browser is most likely to block, and this is the one moment
      // the client must not be interrupted.
      window.location.assign(res.url);
    } catch {
      setError(t("portal.invoices.payFailed"));
      setBusy(false);
    }
  }

  if (!detail) return <DocSkeleton label={t("common.loading")} />;

  const money = (cents: number) => fmtAmount(cents, detail.currency, locale);
  const owed = outstandingCents(detail);
  const late = isOverdue(detail);
  const pdf = pdfHref(`/portal/invoices/${detail.id}/pdf`);
  // Drawn only on the server's word. The client never infers it from the
  // status and the balance.
  const canPay = payable?.available === true;

  const pdfButton = (className?: string) => (
    // Demoted to an outline when there is a card button beside it: two solid
    // buttons side by side give no steer, and the one we want reached for is
    // the one that settles the invoice.
    <Button asChild size="lg" variant={canPay ? "outline" : "primary"} className={className}>
      <a href={pdf} target="_blank" rel="noreferrer">
        <Download />
        {t("portal.invoices.downloadPdf")}
      </a>
    </Button>
  );
  const payButton = (className?: string) => (
    <Button size="lg" loading={busy} onClick={() => void pay()} className={className}>
      <CreditCard />
      {t("portal.invoices.payAmount", { amount: fmtAmount(payable!.outstandingCents, payable!.currency, locale) })}
    </Button>
  );

  return (
    <div>
      <PageHead
        back={{ label: t("portal.invoices.back"), onClick: onBack }}
        title={`${t("portal.invoices.one")} ${detail.number}`}
        badge={
          <Badge variant={invoiceStatusVariant(detail.status)}>
            {late && <AlertCircle className="size-3" />}
            {t(`portal.invoiceStatus.${detail.status}`)}
          </Badge>
        }
        description={[detail.title, detail.client.name].filter(Boolean).join(" · ")}
      />

      <DocLayout
        paper={
          <Card className="p-5 md:p-8">
            <dl className="flex flex-wrap gap-x-12 gap-y-4">
              <Fact label={t("portal.invoices.issued")} value={fmtDate(detail.issueDate, locale)} />
              <Fact label={t("portal.invoices.due")} value={fmtDate(detail.dueDate, locale)} />
              <Fact label={t("portal.client")} value={detail.client.name} />
            </dl>

            <div className="mt-8">
              <LineItems lines={detail.lineItems} currency={detail.currency} />
            </div>

            <Totals
              className="mt-6"
              rows={[
                { label: t("portal.invoices.subtotal"), value: money(detail.subtotalCents) },
                ...(detail.discountCents > 0
                  ? [{ label: t("portal.invoices.discount"), value: `−${money(detail.discountCents)}` }]
                  : []),
                // Left off a document that charges no VAT - see `showVat` on the
                // detail type. An absent flag means yes, so an older API still
                // prints the row it always did.
                ...(detail.showVat !== false
                  ? [{ label: t("portal.invoices.tax"), value: money(detail.taxCents) }]
                  : []),
                { label: t("portal.invoices.grandTotal"), value: money(detail.totalCents), strong: true },
                ...(detail.paidCents > 0 ? [{ label: t("portal.invoices.paid"), value: money(detail.paidCents) }] : []),
                ...(detail.paidCents > 0 && owed > 0
                  ? [{ label: t("portal.invoices.outstanding"), value: money(owed), strong: true }]
                  : []),
              ]}
            />

            {(detail.notes || detail.terms) && (
              <div className="mt-8 grid gap-6 border-t border-border pt-6 sm:grid-cols-2">
                {detail.notes && <Prose title={t("portal.notes")} text={detail.notes} />}
                {detail.terms && <Prose title={t("portal.terms")} text={detail.terms} />}
              </div>
            )}
          </Card>
        }
        lead={
            <Card className="p-6">
              {owed > 0 ? (
                <Figure
                  size="lg"
                  label={t("portal.invoices.toPay")}
                  value={money(owed)}
                  hint={
                    detail.dueDate && (
                      <span className="flex items-center gap-2">
                        {late
                          ? <AlertCircle className="size-4 shrink-0 text-destructive" />
                          : <Clock className="size-4 shrink-0" />}
                        {t("portal.invoices.due")} {fmtDate(detail.dueDate, locale)}
                        {" · "}
                        {relativeDay(detail.dueDate, locale, now)}
                      </span>
                    )
                  }
                />
              ) : (
                <Figure
                  size="lg"
                  label={t("portal.invoices.grandTotal")}
                  value={money(detail.totalCents)}
                  hint={
                    detail.status === "paid" && (
                      <span className="flex items-center gap-2">
                        <CircleCheck className="size-4 shrink-0 text-success" />
                        {t("portal.invoiceStatus.paid")}
                      </span>
                    )
                  }
                />
              )}

              {/* Under `lg` the same two buttons sit in the bar at the bottom
                  of the screen, so they are not drawn twice. */}
              <div className="mt-5 flex flex-col gap-2.5 max-lg:hidden">
                {canPay && payButton()}
                {pdfButton()}
              </div>
              {canPay && <p className="mt-4 text-pretty text-sm text-foreground/70">{t("portal.invoices.payHint")}</p>}
              {error && <p role="alert" className="mt-3 text-sm text-destructive">{error}</p>}
            </Card>
        }
        rail={
            (detail.payments.length > 0 || owed > 0) && (
              <Card className="p-6">
                <h2 className="font-bold tracking-tight">{t("portal.invoices.paymentsTitle")}</h2>
                {detail.payments.length === 0 ? (
                  <p className="mt-2 text-pretty text-sm text-foreground/70">{t("portal.invoices.paymentsEmpty")}</p>
                ) : (
                  <ul className="mt-3 divide-y divide-border">
                    {detail.payments.map((p, i) => (
                      // The index is the key on purpose: a payment row carries
                      // no id, and two equal payments on one day are two real,
                      // indistinguishable rows. The list is never reordered.
                      <li key={i} className="flex items-baseline justify-between gap-3 py-2.5 text-sm">
                        <span className="min-w-0">
                          <span className="block tabular-nums">{fmtDate(p.paidAt, locale)}</span>
                          <span className="block truncate text-xs text-foreground/70">{methodLabel(p, t)}</span>
                        </span>
                        <span className="font-semibold tabular-nums">{fmtAmount(p.amountCents, p.currency, locale)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            )
        }
        phoneBar={
          canPay ? (
            <>
              <Button asChild size="lg" variant="outline" className="px-4">
                <a href={pdf} target="_blank" rel="noreferrer" aria-label={t("portal.invoices.downloadPdf")}>
                  <Download />
                  PDF
                </a>
              </Button>
              {payButton("min-w-0 flex-1 px-4")}
            </>
          ) : (
            pdfButton("flex-1")
          )
        }
      />
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-semibold uppercase tracking-wider text-foreground/70">{label}</dt>
      <dd className="mt-1 font-semibold tabular-nums">{value}</dd>
    </div>
  );
}

/** A block of free text from the document, with the line breaks it was typed with. */
function Prose({ title, text }: { title: string; text: string }) {
  return (
    <div className="min-w-0">
      <h2 className="text-sm font-bold">{title}</h2>
      <p className="mt-1.5 max-w-prose whitespace-pre-wrap text-sm text-foreground/70">{text}</p>
    </div>
  );
}

/**
 * How a payment arrived, in words a client reads.
 *
 * `Payment.method` is a stored token, not copy: the two built-in methods write
 * fixed strings (one of them an internal provider name) and everything else is
 * a label the desk typed. The two built-ins get a catalogue word; a custom
 * label is printed as the desk wrote it, because that IS its name.
 */
function methodLabel(p: PortalPayment, t: Translate): string {
  const method =
    p.method === PAYMENT_METHOD_TOKEN.card
      ? t("portal.invoices.methodCard")
      : p.method === PAYMENT_METHOD_TOKEN.bankTransfer
        ? t("portal.invoices.methodTransfer")
        : p.method;
  return [method, p.reference].filter(Boolean).join(" · ");
}
